/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * The condition vocabulary, in one place.
 *
 * Two features ask the same question — "is this value X?" — and must ask it the
 * same way: a button's "Show only when", and a value chosen by condition during
 * submission. The server answers both through a single function
 * (`evaluate_condition`), so the builder has to offer a single list. Two copies
 * would let the screen offer an operator the server refuses, or read a rule
 * back differently from the way it was written.
 *
 * Nothing here is new — it is the list ActionButtonsSection already used.
 */

export const CONDITION_OPS = [
  { key: 'eq',       label: 'is' },
  { key: 'neq',      label: 'is not' },
  { key: 'in',       label: 'is one of' },
  { key: 'is_set',   label: 'has any value' },
  { key: 'is_blank', label: 'is empty' },
];

// Operators that need something typed in.
export const OPS_WITH_VALUE = ['eq', 'neq', 'in'];

/**
 * The stored shape of a condition's value.
 *
 * `in` holds a LIST; everything else holds a single value. Getting this wrong
 * is not cosmetic: the same expression is re-evaluated server-side, so a list
 * flattened to a string silently changes the outcome.
 */
export function conditionValue(op, value) {
  if (op === 'in') {
    if (Array.isArray(value)) return value;
    return String(value ?? '').split(',').map(v => v.trim()).filter(Boolean);
  }
  return Array.isArray(value) ? (value[0] ?? '') : value;
}

/**
 * The values a column actually permits, or [] when it accepts free text.
 *
 * `allowed_options` is sent raw, in either of the two shapes found in the wild:
 * plain CSV ("A,B,C") and a Python list repr ("['A', 'B']"). Both are normalised
 * here so a caller sees one list either way — the serializer sends it precisely
 * so a show-when rule can offer the field's real values instead of asking
 * someone to type them from memory.
 */
export function allowedValuesOf(recordColumns, columnName) {
  const col = (recordColumns || []).find(c => c.name === columnName);
  const raw = col && col.allowed_options;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(v => String(v).trim()).filter(Boolean);
  return String(raw)
    .replace(/^\s*\[|\]\s*$/g, '')
    .split(',')
    .map(v => v.trim().replace(/^['"]|['"]$/g, ''))
    .filter(Boolean);
}

/**
 * The typed values this column cannot hold.
 *
 * Compared case-insensitively because that is how the rule is actually
 * evaluated, on both the client and the server — flagging "open" against "OPEN"
 * would warn about something that works perfectly.
 *
 * A column with no declared options returns nothing: free text is not a mistake,
 * and warning about it would train people to ignore the warning.
 */
export function unknownConditionValues(allowed, value) {
  if (!allowed.length) return [];
  const permitted = allowed.map(v => v.toLowerCase());
  return String(value || '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
    .filter(v => !permitted.includes(v.toLowerCase()));
}
